import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { tokens } from '../theme.js'
import { LoginLayout, WelcomeSection, BrandLogo, WelcomeHeading, LoginCard, FormField, FormFieldLabel } from './styles/LoginPageStyles.js'
import { useAuth } from '../auth/AuthContext.js'
import { api } from '../api/client.js'
import { errorMessage } from '../api/errors.js'
import { usePublicConfig } from '../hooks/usePublicConfig.js'
import {
  PageTitle,
  BodyText,
  Button,
  TextField,
  ErrorText,
  TextButton,
  FlexColumn,
  PasswordField,
  PasswordToggle
} from '../components/styles/index.js'
import citadelLogo from '../assets/citadel-logo.svg'

const TITLES = { login: 'Sign in', register: 'Create account', forgot: 'Reset your password', reset: 'Choose a new password' }

export default function LoginPage() {
  const { login, register } = useAuth()
  const navigate = useNavigate()
  const features = usePublicConfig()
  const canRegister = Boolean(features?.registrationEnabled)
  const canReset = Boolean(features?.passwordResetEnabled)
  const [mode, setMode] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)

  function go(next) {
    setMode(next)
    setError('')
    setNotice('')
  }

  async function submit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'login') {
        await login(email, password)
        navigate('/')
      } else if (mode === 'register') {
        await register(email, password)
        navigate('/')
      } else if (mode === 'forgot') {
        const { data } = await api.post('/api/auth/password-reset/request', { email })
        if (data.resetToken) {
          setResetToken(data.resetToken)
          setMode('reset')
          setNotice('Enter a new password to finish.')
        } else setNotice('If that email is registered, a reset link has been sent.')
      } else if (mode === 'reset') {
        await api.post('/api/auth/password-reset/confirm', { token: resetToken, newPassword })
        setMode('login')
        setPassword('')
        setNewPassword('')
        setNotice('Your password has been reset. Sign in with your new password.')
      }
    } catch (err) {
      setError(errorMessage(err) || 'something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <LoginLayout>
      <WelcomeSection>
        <BrandLogo data-testid="auth-brand">
          <img src={citadelLogo} alt="Citadel" />
        </BrandLogo>
        <WelcomeHeading>Welcome back!</WelcomeHeading>
        <BodyText>Secure document redaction for teams that handle sensitive information.</BodyText>
      </WelcomeSection>
      <LoginCard>
        <PageTitle data-testid="auth-title">{TITLES[mode]}</PageTitle>
        <div style={{ height: 6 }} />
        <div style={{ height: 24 }} />
        <form onSubmit={submit} autoComplete="off">
          <FlexColumn gap="16px">
            {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
              <FormField>
                <FormFieldLabel htmlFor="auth-email">Email</FormFieldLabel>
                <TextField
                  id="auth-email"
                  type="email"
                  name="email"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  fullWidth
                  onChange={e => setEmail(e.target.value)}
                  inputProps={{ 'data-testid': 'email' }}
                />
              </FormField>
            )}
            {(mode === 'login' || mode === 'register') && (
              <FormField>
                <FormFieldLabel htmlFor="auth-password">Password</FormFieldLabel>
                <PasswordField>
                  <TextField
                    id="auth-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="new-password"
                    autoCorrect="off"
                    spellCheck={false}
                    value={password}
                    fullWidth
                    onChange={e => setPassword(e.target.value)}
                    inputProps={{ 'data-testid': 'password' }}
                  />
                  <PasswordToggle
                    shown={showPassword}
                    onClick={() => setShowPassword(v => !v)}
                    label={showPassword ? 'Hide password' : 'Show password'}
                  />
                </PasswordField>
              </FormField>
            )}
            {mode === 'reset' && (
              <>
                <FormField>
                  <FormFieldLabel htmlFor="auth-reset-token">Reset token:</FormFieldLabel>
                  <TextField
                    id="auth-reset-token"
                    value={resetToken}
                    fullWidth
                    onChange={e => setResetToken(e.target.value)}
                    inputProps={{ 'data-testid': 'reset-token' }}
                  />
                </FormField>
                <FormField>
                  <FormFieldLabel htmlFor="auth-new-password">New password</FormFieldLabel>
                  <PasswordField>
                    <TextField
                      id="auth-new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      name="new-password"
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                      value={newPassword}
                      fullWidth
                      onChange={e => setNewPassword(e.target.value)}
                      inputProps={{ 'data-testid': 'new-password' }}
                    />
                    <PasswordToggle
                      shown={showNewPassword}
                      onClick={() => setShowNewPassword(v => !v)}
                      label={showNewPassword ? 'Hide new password' : 'Show new password'}
                    />
                  </PasswordField>
                </FormField>
              </>
            )}
            {notice && (
              <BodyText data-testid="auth-notice" style={{ fontSize: 13, color: tokens.colors.primary }}>
                {notice}
              </BodyText>
            )}
            {error && <ErrorText data-testid="auth-error">{error}</ErrorText>}
            <Button type="submit" loading={loading} width="100%" data-testid="submit">
              {mode === 'login'
                ? 'Sign in'
                : mode === 'register'
                  ? 'Create account'
                  : mode === 'forgot'
                    ? 'Send reset instructions'
                    : 'Reset password'}
            </Button>
          </FlexColumn>
        </form>
        <div style={{ height: 20 }} />
        <FlexColumn gap="8px">
          {mode === 'login' && (canRegister || canReset) && (
            <BodyText style={{ fontSize: 14 }}>
              {canRegister && (
                <>
                  No account yet?{' '}
                  <TextButton type="button" data-testid="toggle-mode" onClick={() => go('register')}>
                    Create one
                  </TextButton>
                </>
              )}
              {canRegister && canReset && '  ·  '}
              {canReset && (
                <TextButton type="button" data-testid="forgot-link" onClick={() => go('forgot')}>
                  Forgot password?
                </TextButton>
              )}
            </BodyText>
          )}
          {mode === 'register' && (
            <BodyText style={{ fontSize: 14 }}>
              Already have an account?{' '}
              <TextButton type="button" data-testid="toggle-mode" onClick={() => go('login')}>
                Sign in
              </TextButton>
            </BodyText>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <BodyText style={{ fontSize: 14 }}>
              <TextButton type="button" data-testid="back-to-login" onClick={() => go('login')}>
                ← Back to sign in
              </TextButton>
            </BodyText>
          )}
        </FlexColumn>
      </LoginCard>
    </LoginLayout>
  )
}
